import { isNotNull, useCreateWorkLocationAssignment, WorkLocationFragment } from '@alice-financial/alice-sdk'
import {
  Button,
  Container,
  DialogActions,
  Divider,
  FullDialog,
  TextFieldController,
  Typography,
} from '@alice-financial/pretext-ui'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useOrg } from '../organization/useOrg'
import { PaygroupAssignmentCheckboxes } from './PaygroupAssignmentCheckboxes'

// Types for the form (simplified - only for creating new paygroups)
export type PaygroupFormInputs = {
  name: string
  marketingFriendlyName: string
}

type PaygroupFormProps = {
  open: boolean
  onClose: () => void
  location: WorkLocationFragment
}

/**
 * Assign existing pay gruop to the specified location, or create a new pay group
 * @param param0
 * @returns
 */
export const PaygroupForm: React.FC<PaygroupFormProps> = ({ open, onClose, location }) => {
  const selectedPayGroups = location.employersInfo || []
  const org = useOrg()
  const isPayrollConnectionConfirmed = Boolean(org?.payrollConnection.payrollConnectedAt)
  const mayCreateNewPaygroup = !isPayrollConnectionConfirmed && !org?.isOnboarded

  const availablePayGroups = React.useMemo(() => {
    return org?.employers.filter(isNotNull) || []
  }, [org?.employers])

  const { control, handleSubmit, reset } = useForm<PaygroupFormInputs>({
    defaultValues: {
      name: '',
      marketingFriendlyName: '',
    },
  })

  // Set up the mutation hook for creating new paygroups
  const { mutate: createAssignment, isLoading } = useCreateWorkLocationAssignment(org?.id || 0, {
    onSuccess: () => {
      reset()
      onClose()
    },
    onError: (error) => {
      console.error('Failed to create work location assignment:', error)
    },
  })

  // Handle form submission for new paygroup creation
  const onSubmit = (data: PaygroupFormInputs) => {
    if (data.name.trim()) {
      createAssignment({
        input: {
          workLocationId: location.id,
          employerName: data.name.trim(),
          employerMarketingFriendlyName: data.marketingFriendlyName.trim(),
        },
      })
    } else {
      // If no new name, just close the form
      reset()
      onClose()
    }
  }

  return (
    <FullDialog
      open={open}
      onClose={onClose}
      title={
        <>
          <Typography variant="h2">Assign pay group{availablePayGroups.length > 1 ? '(s)' : ''}</Typography>
          <Typography variant="body2">
            <strong>Location:</strong> {location.name}
          </Typography>
        </>
      }
    >
      <Container>
        {availablePayGroups.length > 0 && (
          <>
            <Typography variant="body2">
              <strong>Assign existing pay group(s):</strong>
            </Typography>
            <PaygroupAssignmentCheckboxes
              workLocationId={location.id}
              availablePayGroups={availablePayGroups}
              selectedPayGroups={selectedPayGroups}
            />
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {mayCreateNewPaygroup && (
          <>
            {availablePayGroups.length > 0 && (
              <Typography variant="body2" gutterBottom>
                <strong>Create a new pay group</strong>
              </Typography>
            )}
            <Typography variant="body2" gutterBottom>
              This will create a new pay group and assign it to this location.
              <br />
              We will confirm this pay group in your payroll system before launch.
            </Typography>

            {/* Form for creating new paygroups */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <TextFieldController
                name="name"
                control={control}
                label="Pay group name"
                variant="outlined"
                required={availablePayGroups.length === 0}
                fullWidth
                autoComplete="off"
                helperText="The pay group name as it appears in your payroll system"
                sx={{ mb: 3 }}
              />
              <TextFieldController
                name="marketingFriendlyName"
                control={control}
                label="Friendly name"
                variant="outlined"
                fullWidth
                autoComplete="off"
                helperText="A name for the pay group that employees recognize - defaults to the pay group name"
              />
              <DialogActions sx={{ mt: 3 }}>
                <Button
                  type="reset"
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </form>
          </>
        )}
      </Container>
    </FullDialog>
  )
}
